import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { ReactComponent as TriangleIconBase } from '../../assets/triangle-outlined.svg';
import { ColorToken } from '../../style/types';
import { getColor, space, fontSize, radius } from '../../style/utils';
import { Flex, FlexColumn } from '../Flex';

export const timestampFormat = 'MMM DD LT';

export type Status = 'pass' | 'warn' | 'fail';
export type AreaStatus = Status | 'learn' | 'update' | 'segmented';
export const areaStatuses = [
  'pass',
  'warn',
  'fail',
  'learn',
  'update',
  'segmented',
];

// Picks the color for the anomaly band of a freshness chart.
// When a test passed but still has anomalous data (failure grace suppressed
// the alert), fall back to the configured severity so the band matches the
// color the failure would have used.
export const resolveAnomalyColor = (
  status: string,
  severity: 'warn' | 'error' | undefined,
  anomalyPercent: number,
): AreaStatus => {
  if (status === 'pass' && anomalyPercent > 0) {
    return severity === 'warn' ? 'warn' : 'fail';
  }
  return status as AreaStatus;
};

export type XPos = 'left' | 'right' | 'center';
export type YPos = 'top' | 'bottom';

export const poleColorMap: Record<string, ColorToken> = {
  warn: 'status.warning.strong',
  fail: 'status.error.soft',
  end: 'chart.marker',
  start: 'accent.primary',
};

export const statusColorMap: Record<string, ColorToken> = {
  pass: 'status.success.main',
  warn: 'status.warning.strong',
  fail: 'status.error.soft',
  error: 'status.error.soft',
  no_data: 'chart.idle',
  skipped: 'chart.idle',
  learn: 'chart.idle',
  update: 'surface.base',
};

export const LegendWrapper = styled(Flex)`
  gap: ${space('xxl')};
  align-items: center;
`;

export const LegendItem = styled(Flex)`
  gap: ${space('md')};
  align-items: center;

  > p {
    font-size: ${fontSize('sm')};
  }
`;

export const Divider = styled.div`
  height: 18px;
  width: 1px;
  background: ${({ theme }) => theme.palette.chart.muted};
`;

export const Wrapper = styled(FlexColumn)`
  width: 100%;
  padding: ${space('lg', 'lg', 'xxxl', 'lg')};
  border-radius: ${radius('md')};
  background: ${({ theme }) => theme.palette.surface.subtle};
  border: 1px solid ${({ theme }) => theme.palette.border.muted};
  gap: ${space('lg')};

  * {
    user-select: none !important;
  }
`;

export const TimestampLabel = styled(Typography)<{
  $color: 'start' | 'end';
  $pos: YPos;
}>`
  font-size: ${fontSize('sm')};
  color: ${({ $color, theme }) => getColor(theme, poleColorMap[$color])};
  position: absolute;
  ${({ $pos }) => ($pos === 'bottom' ? 'bottom: -22px' : 'top: -26px')};
  width: max-content;
`;

export const ChartBar = styled(Flex)<{ $zoom?: number; withSelect?: boolean }>`
  width: ${({ $zoom }) => 100 * ($zoom || 1)}%;
  height: 60px;
  background: ${({ theme }) => theme.palette.surface.base};
  justify-content: space-evenly;
  position: relative;

  ${({ withSelect }) =>
    withSelect &&
    css`
      > div {
        cursor: crosshair;
      }
    `}
`;

export const Chart = styled(FlexColumn)<{ $scroll?: boolean }>`
  gap: ${space('sm')};
  padding: ${space('lg', 0)} ${({ $scroll }) => ($scroll ? 24 : 16)}px 0;
  ${({ $scroll }) => ($scroll ? 'overflow-x: auto;' : '')}
  ${({ $scroll }) => ($scroll ? 'overflow-y: hidden;' : '')}
`;

export const Pole = styled(Flex)<{
  $color: 'warn' | 'fail' | 'end' | 'start';
  $hasLabel?: boolean;
  $width?: number;
}>`
  width: ${({ $width }) => $width || 2}px;
  height: ${({ $hasLabel }) => ($hasLabel ? 'calc(100% - 10px)' : '100%')};
  background: ${({ $color, theme }) => getColor(theme, poleColorMap[$color])};
  z-index: 1;
`;

export const PoleWrapper = styled(Flex)<{
  $right: number;
}>`
  position: absolute;
  height: 100%;
  right: ${({ $right }) => `calc(${$right}% - 1px)`};
  z-index: 1;
  animation: fadeAnimation 0.2s forwards;
  @keyframes fadeAnimation {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

export const slideAnimation = (
  value: number,
  property: string = 'width',
) => keyframes`
  from {
    ${property}: 0;
  }
  to {
    ${property}: ${value}%;
  }
`;

export const TriangleIcon = styled(TriangleIconBase)`
  position: absolute;
  top: -9px;
  left: -6px;
`;

export const ChartEdgeBoundary = styled(Flex)`
  position: absolute;
  width: 1px;
  height: 150%;
  left: 0;
`;

export const Area = styled(Flex)<{
  $color: AreaStatus;
  $width: number;
  $right?: number;
  $opacity?: number;
}>`
  height: 100%;
  background: ${({ $color, theme }) =>
    $color === 'segmented' ? 'unset' : getColor(theme, statusColorMap[$color])};
  position: absolute;
  opacity: ${({ $opacity }) => ($opacity !== undefined ? $opacity : 0.3)};
  right: ${({ $right: $offsetRight }) =>
    $offsetRight ? `${$offsetRight}%` : 0};
  z-index: 0;
  width: ${({ $width }) => $width}%;
  animation: ${({ $width }) => slideAnimation($width)} 0.6s ease-in-out forwards;
`;

export const EndPole = React.forwardRef(
  (
    {
      timestamp,
      showTimestamp,
      execTimeLabelRef,
      execTimeLabelPos,
    }: {
      timestamp: dayjs.Dayjs;
      showTimestamp: boolean;
      execTimeLabelRef?: any;
      execTimeLabelPos: YPos;
    },
    ref: any,
  ) => (
    <PoleWrapper $right={0}>
      <TriangleIcon />
      <Pole ref={ref} $color="end" />
      {showTimestamp && (
        <TimestampLabel
          ref={execTimeLabelRef}
          sx={{ right: execTimeLabelPos === 'top' ? '-5px' : 0 }}
          $color="end"
          $pos={execTimeLabelPos}
        >
          {timestamp.format(timestampFormat)}
        </TimestampLabel>
      )}
    </PoleWrapper>
  ),
);
